import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Pagination } from '../components/ui/Pagination';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { Button } from '../components/ui/Button';
import {
  FileSearch,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  AlertTriangle,
  AlertCircle,
  Info,
  Ban,
  RotateCcw,
  Settings,
  Activity,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { AdminAnalysis, AnalysisStatus } from '../types/admin';
import TelemetryDetailModal from '../components/telemetry-detail-modal';
import { formatTokens } from '../utils/format-tokens';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds}s`;
};

const VALID_STATUSES: AnalysisStatus[] = ['pending', 'running', 'completed', 'failed', 'cancelled'];

const Analyses: React.FC = () => {
  const [page, setPage] = useState(1);
  const [statusModalAnalysis, setStatusModalAnalysis] = useState<AdminAnalysis | null>(null);
  const [telemetryAnalysisId, setTelemetryAnalysisId] = useState<string | null>(null);
  const pageSize = 20;
  const queryClient = useQueryClient();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['analysis-stats'],
    queryFn: () => adminApiClient.getAnalysisStats(),
  });

  const { data: analyses, isLoading: analysesLoading } = useQuery({
    queryKey: ['analyses', page, pageSize],
    queryFn: () => adminApiClient.getAnalyses({ page, page_size: pageSize }),
  });

  const cancelMutation = useMutation({
    mutationFn: (analysisId: string) => adminApiClient.cancelAnalysis(analysisId),
    onSuccess: () => {
      toast.success('Analysis cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
      queryClient.invalidateQueries({ queryKey: ['analysis-stats'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Failed to cancel analysis';
      toast.error(message);
    },
  });

  const requeueMutation = useMutation({
    mutationFn: (analysisId: string) => adminApiClient.requeueAnalysis(analysisId),
    onSuccess: () => {
      toast.success('Analysis requeued successfully');
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
      queryClient.invalidateQueries({ queryKey: ['analysis-stats'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Failed to requeue analysis';
      toast.error(message);
    },
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ analysisId, status }: { analysisId: string; status: AnalysisStatus }) =>
      adminApiClient.setAnalysisStatus(analysisId, { status }),
    onSuccess: (_, variables) => {
      toast.success(`Analysis status set to ${variables.status}`);
      setStatusModalAnalysis(null);
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
      queryClient.invalidateQueries({ queryKey: ['analysis-stats'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Failed to set analysis status';
      toast.error(message);
    },
  });

  const handleCancel = (analysis: AdminAnalysis) => {
    if (window.confirm(`Are you sure you want to cancel analysis for "${analysis.repository_name}"?`)) {
      cancelMutation.mutate(analysis.id);
    }
  };

  const handleRequeue = (analysis: AdminAnalysis) => {
    if (window.confirm(`Requeue analysis for "${analysis.repository_name}"? This will reset it to pending status for re-processing.`)) {
      requeueMutation.mutate(analysis.id);
    }
  };

  const handleSetStatus = (status: AnalysisStatus) => {
    if (statusModalAnalysis) {
      setStatusMutation.mutate({ analysisId: statusModalAnalysis.id, status });
    }
  };

  const canCancel = (status: string) => {
    return status === 'pending' || status === 'processing';
  };

  const canRequeue = (status: string) => {
    return status === 'failed' || status === 'cancelled';
  };

  const isAnyMutating = cancelMutation.isPending || requeueMutation.isPending || setStatusMutation.isPending;

  if (statsLoading || analysesLoading) {
    return <Loading message="Loading analyses..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Analyses</h1>
        <p className="text-gray-400 mt-1">
          View all security analyses across the platform
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Analyses"
            value={stats.total.toLocaleString()}
            icon={FileSearch}
          />
          <StatCard
            title="Completed"
            value={stats.by_status.completed.toLocaleString()}
            subtitle={`${((stats.by_status.completed / stats.total) * 100).toFixed(1)}% success rate`}
            icon={CheckCircle}
          />
          <StatCard
            title="Avg Duration"
            value={formatDuration(stats.avg_duration_seconds)}
            icon={Clock}
          />
          <StatCard
            title="This Month"
            value={stats.this_month_count.toLocaleString()}
            trend={{ value: stats.this_week_count, label: 'this week', isPositive: true }}
          />
        </div>
      )}

      {/* Status Breakdown */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="flex items-center space-x-3">
            <div className="p-2 bg-green-900/50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.by_status.completed}</p>
              <p className="text-sm text-gray-400">Completed</p>
            </div>
          </Card>
          <Card className="flex items-center space-x-3">
            <div className="p-2 bg-blue-900/50 rounded-lg">
              <Loader className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.by_status.processing}</p>
              <p className="text-sm text-gray-400">Processing</p>
            </div>
          </Card>
          <Card className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-900/50 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.by_status.pending}</p>
              <p className="text-sm text-gray-400">Pending</p>
            </div>
          </Card>
          <Card className="flex items-center space-x-3">
            <div className="p-2 bg-red-900/50 rounded-lg">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.by_status.failed}</p>
              <p className="text-sm text-gray-400">Failed</p>
            </div>
          </Card>
        </div>
      )}

      {/* Analyses Table */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700">
          <CardTitle>All Analyses</CardTitle>
        </div>

        {analyses && analyses.data.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Repository</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Depth</TableCell>
                  <TableCell header>LLM</TableCell>
                  <TableCell header>Findings</TableCell>
                  <TableCell header>Tokens</TableCell>
                  <TableCell header>Duration</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyses.data.map((analysis) => (
                  <TableRow key={analysis.id}>
                    <TableCell>
                      <span className="font-medium text-white">{analysis.repository_name}</span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={analysis.status} />
                    </TableCell>
                    <TableCell>
                      <DepthBadge depth={analysis.depth} />
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300 text-xs">{analysis.llm_agent}</span>
                    </TableCell>
                    <TableCell>
                      <FindingsSummary analysis={analysis} />
                    </TableCell>
                    <TableCell>
                      {analysis.token_inputs > 0 || analysis.token_outputs > 0 ? (
                        <div className="text-xs">
                          <span className="text-blue-400">{formatTokens(analysis.token_inputs)}</span>
                          <span className="text-gray-600 mx-1">/</span>
                          <span className="text-green-400">{formatTokens(analysis.token_outputs)}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400">
                        {analysis.duration_seconds
                          ? formatDuration(analysis.duration_seconds)
                          : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400">
                        {format(new Date(analysis.created_at), 'MMM d, HH:mm')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {canCancel(analysis.status) && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleCancel(analysis)}
                            loading={cancelMutation.isPending && cancelMutation.variables === analysis.id}
                            disabled={isAnyMutating}
                          >
                            <Ban className="w-3 h-3 mr-1" />
                            Cancel
                          </Button>
                        )}
                        {canRequeue(analysis.status) && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleRequeue(analysis)}
                            loading={requeueMutation.isPending && requeueMutation.variables === analysis.id}
                            disabled={isAnyMutating}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Requeue
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setTelemetryAnalysisId(analysis.id)}
                          title="View telemetry"
                        >
                          <Activity className="w-3 h-3 mr-1" />
                          Telemetry
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setStatusModalAnalysis(analysis)}
                          disabled={isAnyMutating}
                          title="Set status"
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          Status
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={analyses.page}
              totalPages={analyses.total_pages}
              totalItems={analyses.total}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </>
        ) : (
          <div className="p-12 text-center text-gray-400">
            No analyses found
          </div>
        )}
      </Card>

      {/* Telemetry Modal */}
      {telemetryAnalysisId && (
        <TelemetryDetailModal
          analysisId={telemetryAnalysisId}
          onClose={() => setTelemetryAnalysisId(null)}
        />
      )}

      {/* Status Change Modal */}
      {statusModalAnalysis && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Set Analysis Status</h3>
              <button
                onClick={() => setStatusModalAnalysis(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Change status for: <span className="text-white">{statusModalAnalysis.repository_name}</span>
            </p>
            <p className="text-gray-400 text-sm mb-4">
              Current status: <StatusBadge status={statusModalAnalysis.status} />
            </p>
            <div className="space-y-2">
              {VALID_STATUSES.map((status) => (
                <button
                  key={status}
                  onClick={() => handleSetStatus(status)}
                  disabled={setStatusMutation.isPending || status === statusModalAnalysis.status}
                  className={`w-full p-3 rounded-lg text-left flex items-center justify-between transition-colors ${
                    status === statusModalAnalysis.status
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-800 hover:bg-gray-700 text-white'
                  }`}
                >
                  <span className="capitalize">{status}</span>
                  {status === 'pending' && (
                    <span className="text-xs text-gray-400">Clears timing/error fields</span>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                variant="ghost"
                onClick={() => setStatusModalAnalysis(null)}
                disabled={setStatusMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const variants: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    completed: 'success',
    processing: 'info',
    running: 'info',
    pending: 'warning',
    failed: 'error',
    cancelled: 'default',
  };

  return (
    <Badge variant={variants[status] || 'default'}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const DepthBadge: React.FC<{ depth: string }> = ({ depth }) => {
  const variants: Record<string, 'default' | 'info' | 'pro'> = {
    quick: 'default',
    standard: 'info',
    deep: 'pro',
  };

  return (
    <Badge variant={variants[depth] || 'default'}>
      {depth.charAt(0).toUpperCase() + depth.slice(1)}
    </Badge>
  );
};

const FindingsSummary: React.FC<{ analysis: any }> = ({ analysis }) => {
  if (analysis.findings_count === 0) {
    return <span className="text-gray-500">-</span>;
  }

  return (
    <div className="flex items-center space-x-2">
      {analysis.critical_count > 0 && (
        <span className="flex items-center text-red-400 text-xs">
          <AlertCircle className="w-3 h-3 mr-1" />
          {analysis.critical_count}
        </span>
      )}
      {analysis.high_count > 0 && (
        <span className="flex items-center text-orange-400 text-xs">
          <AlertTriangle className="w-3 h-3 mr-1" />
          {analysis.high_count}
        </span>
      )}
      {analysis.medium_count > 0 && (
        <span className="flex items-center text-yellow-400 text-xs">
          {analysis.medium_count}M
        </span>
      )}
      {analysis.low_count > 0 && (
        <span className="flex items-center text-blue-400 text-xs">
          {analysis.low_count}L
        </span>
      )}
      {analysis.info_count > 0 && (
        <span className="flex items-center text-gray-400 text-xs">
          <Info className="w-3 h-3 mr-1" />
          {analysis.info_count}
        </span>
      )}
    </div>
  );
};

export default Analyses;
